// React
import React, { ChangeEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Libs
import { Button, Form, Modal } from 'semantic-ui-react';

// Services
import SaveWorkflowService from '../../../services/SaveWorkflow.service';
import { LoaderState } from '../../../types/LoaderState';
import LeadsLoadingComponent from '../list/LeadsLoading.component';

// Types
type LeadWorkflowModalComponentProps = {
    leadId: string | undefined;
    task: any;
    active: boolean;
    onClose: (v?: boolean) => void;
    token: string;
};

export default function LeadWorkflowModalComponent(props: LeadWorkflowModalComponentProps): React.ReactElement {
    const [isOpen, setIsOpen] = useState(false);
    const [fields, setFields] = useState<Array<string>>();
    const [fieldValue, setFieldValue] = useState<{ [key: string]: any } | null>();
    const [contractId, setContractId] = useState<string>();
    const [fieldError, setFieldError] = useState(false);
    const [fieldErrorContent, setFieldErrorContent] = useState<string>('lead.fields.errorEmpty');
    const [loaderState, setLoaderState] = useState<LoaderState>({ state: '' });
    const { t } = useTranslation();

    const { task, parameterDescriptors } = props.task;

    const service = new SaveWorkflowService(props.leadId);

    useEffect(() => {
        setIsOpen(props.active);
    }, [props.active]);

    useEffect(() => {
        setFields(Object.keys(parameterDescriptors));
    }, [parameterDescriptors]);

    function onAbort() {
        props.onClose();
    }

    function validateNumberField(value: string) {
        const numberRegEx = /^(?!,$)[\d.]+$/;
        const isValid = numberRegEx.test(String(value).toLowerCase());
        if (!isValid) {
            setFieldError(true);
            setFieldErrorContent('lead.fields.errorNumbersOnly');
        } else {
            setFieldError(false);
            setFieldErrorContent('');
        }
        return isValid;
    }

    function onChangeNumberField(_: React.SyntheticEvent, { name, value }: any) {
        onChangeField(name, value);
    }

    function onChangeField(name: string, value: string) {
        if (!value || !String(value).length || value === 'ALL') {
            setFieldValue(null);
            setFieldErrorContent('lead.fields.errorEmpty');
            setFieldError(true);
        } else {
            if (!validateNumberField(value)) {
                setFieldValue(null);
            } else {
                setFieldValue({
                    ...fieldValue,
                    [name]: value,
                });
            }
        }
    }

    function onChangeContractIdField(e: ChangeEvent<HTMLInputElement>) {
        if (e.target.value) {
            onChangeField(e.target.name, e.target.value);
        }
    }

    function onChangeCreditCheck(_: React.SyntheticEvent, name: string, value: 'SUCCESS' | 'REJECT') {
        if (!value || !String(value).length) {
            setFieldValue(null);
        } else {
            setFieldValue({
                ...fieldValue,
                [name]: value,
            });
        }
    }

    function onSubmit() {
        let _fieldValue = fieldValue;
        let errorContent;
        if (task === 'CONTRACT_ACTIVATION' || task === 'CLOSE_LEASMAN_CONTRACT') {
            _fieldValue = { [task]: true };
        }
        if (_fieldValue) {
            if (task === 'ARMADA_CUSTOMER_CREATION') {
                const armadaCustomerId = _fieldValue['armadaCustomerId'];
                _fieldValue['armadaCustomerId'] = armadaCustomerId.replaceAll('.', '');
            }
        } else {
            errorContent = !fieldErrorContent ? 'lead.fields.errorEmpty' : fieldErrorContent;
        }

        if (errorContent) {
            setFieldErrorContent(errorContent);
            setFieldError(true);
        } else {
            service
                .send(
                    {
                        task: task,
                        payload: _fieldValue || {},
                    },
                    props.token
                )
                .then((succeeded: boolean) => {
                    if (succeeded) props.onClose(true);
                });
        }
    }

    function createFieldForNumberInput(ix: number, fieldName: string) {
        return (
            <Form.Input
                key={ix}
                label={t(`lead.fields.${fieldName}`)}
                name={fieldName}
                type="text"
                onChange={onChangeNumberField}
                onFocus={() => setFieldError(false)}
                error={fieldError ? { content: t(fieldErrorContent) } : false}
                data-testid={`LeadWorkflowModal.text-input`}
            />
        );
    }

    function createFieldFor_CREDIT_CHECK(fieldName: string) {
        return (
            <>
                <div>{t('lead.fields.creditCheck')}</div>
                <input type="hidden" id={fieldName} value={fieldValue ? fieldValue[fieldName] : ''} />
                <br />
                <Button
                    onClick={(e) => onChangeCreditCheck(e, fieldName, 'SUCCESS')}
                    style={{ background: 'yellowgreen', width: '303px' }}
                    data-testid={`LeadWorkflowModal.${fieldName}-success`}>
                    {t('lead.fields.creditCheck_SUCCESS')}
                </Button>
                <span style={{ width: '33px', display: 'inline-block' }}></span>
                <Button
                    onClick={(e) => onChangeCreditCheck(e, fieldName, 'REJECT')}
                    style={{ background: 'salmon' }}
                    data-testid={`LeadWorkflowModal.${fieldName}-reject`}>
                    {t('lead.fields.creditCheck_REJECT')}
                </Button>
                <span style={{ width: '33px', display: 'inline-block' }}></span>
                <Button onClick={onAbort}>{t('lead.fields.abort')}</Button>
            </>
        );
    }

    function renderTableBody() {
        // data are loading
        if (loaderState.state !== 'loaded') {
            return <LeadsLoadingComponent colSpan={4} state={loaderState} />;
        }
    }

    function createFieldFor_MASTER_CONTRACT_ASSIGNMENT(ix: number, fieldName: string) {
        return (
            <>
                <>
                    <input
                        value={contractId}
                        key={ix}
                        name={fieldName}
                        type="text"
                        onChange={onChangeContractIdField}
                        onFocus={() => {
                            setFieldError(false);
                            setContractId(undefined);
                        }}
                        data-testid={'LeadWorkflowModal.text-input'}
                    />
                    {fieldError && (
                        <>
                            <br />
                            <div style={{ marginTop: '6px', color: '#9f3a38' }}>{t(fieldErrorContent)}</div>
                            <br />
                        </>
                    )}
                </>
            </>
        );
    }

    return (
        <>
            {isOpen && (
                <Modal onClose={onAbort} open={isOpen} dimmer="blurring">
                    <Modal.Header>{t(`lead.detail.tools.tasks.${task}`)}</Modal.Header>
                    <Modal.Content>
                        <Form onSubmit={onSubmit} id="modalForm">
                            {fields?.length ? (
                                fields.map((key, ix) => {
                                    switch (task) {
                                        case 'ARMADA_CUSTOMER_CREATION':
                                        case 'LEASMAN_CUSTOMER_CREATION':
                                            return createFieldForNumberInput(ix, key);
                                        case 'MASTER_CONTRACT_ASSIGNMENT':
                                            return createFieldFor_MASTER_CONTRACT_ASSIGNMENT(ix, key);
                                        case 'CREDIT_CHECK':
                                            return createFieldFor_CREDIT_CHECK(key);
                                        default:
                                    }
                                })
                            ) : (
                                // EXCEPTION for task CONTRACT_ACTIVATION, and CLOSE_LEASMAN_CONTRACT -- DU-1234
                                <>
                                    {(task === 'CONTRACT_ACTIVATION' || task === 'CLOSE_LEASMAN_CONTRACT') && (
                                        <p>{t('lead.fields.contractActivation')}</p>
                                    )}
                                </>
                            )}
                        </Form>
                    </Modal.Content>
                    {task !== 'CREDIT_CHECK' && (
                        <Modal.Actions>
                            <Button onClick={onAbort}>{t('lead.fields.abort')}</Button>
                            <Button primary type="submit" form="modalForm" data-testid={'LeadWorkflowModal.submit'}>
                                {t('lead.fields.post')}
                            </Button>
                        </Modal.Actions>
                    )}
                </Modal>
            )}
        </>
    );
}
